/**
 * User Mapping Utility
 * Maps API response data to match Swift User model structure
 */

/**
 * Swift User Model Structure (ObjectMapper):
 * func mapping(map: ObjectMapper.Map) {
 *     userName <- map["login"]
 *     token <- map["token"]
 *     phone <- map["phone"]
 *     address <- map["address"]
 *     storeName <- map["store_name"]
 *     avatar <- map["avatar"]
 *     start_use_date <- map["create_date"]
 *     expired_date <- map["expire_date"]
 *     fullName <- map["name"]
 *     privatePlan <- map["plan"]
 *     privateRole <- map["role"]
 * }
 * 
 * Swift User Model Properties:
 * - userName: String?
 * - token: String?
 * - phone: String?
 * - address: String?
 * - storeName: String?
 * - avatar: String?
 * - start_use_date: String?
 * - expired_date: String?
 * - fullName: String?
 * - privatePlan: String?
 * - privateRole: String?
 */

/**
 * Map API response to Swift User model structure
 * @param {Object} apiResponse - Raw API response
 * @returns {Object} Mapped user object matching Swift structure
 */
export const mapUserToSwiftModel = (apiResponse) => {
  if (!apiResponse) return null;

  // Map API fields to Swift User model fields (exact mapping from Swift ObjectMapper)
  const mappedUser = {
    // Swift User Model fields only (exact mapping from Swift ObjectMapper)
    userName: apiResponse.login || null,                    // userName <- map["login"]
    token: apiResponse.token || null,                       // token <- map["token"]
    phone: apiResponse.phone || null,                       // phone <- map["phone"]
    address: apiResponse.address || null,                   // address <- map["address"]
    storeName: apiResponse.store_name || null,              // storeName <- map["store_name"]
    avatar: apiResponse.avatar || null,                     // avatar <- map["avatar"]
    start_use_date: apiResponse.create_date || null,        // start_use_date <- map["create_date"]
    expired_date: apiResponse.expire_date || null,          // expired_date <- map["expire_date"]
    fullName: apiResponse.name || null,                     // fullName <- map["name"]
    privatePlan: apiResponse.plan || null,                  // privatePlan <- map["plan"]
    privateRole: apiResponse.role || null,                  // privateRole <- map["role"]
  };

  return mappedUser;
};

/**
 * Get shop information from mapped user object
 * @param {Object} mappedUser - User object mapped to Swift structure
 * @returns {Object} Shop information object
 */
export const getShopInfoFromUser = (mappedUser) => {
  if (!mappedUser) return null;

  return {
    // Swift User Model fields only (exact mapping from Swift)
    storeName: mappedUser.storeName || '',           // Swift: storeName
    phone: mappedUser.phone || '',                                  // Swift: phone
    address: mappedUser.address || '',                              // Swift: address
    name: mappedUser.fullName || '',                                // Swift: fullName
    role: mappedUser.privateRole || '',                        // Swift: privateRole
    plan: mappedUser.privatePlan || '',                       // Swift: privatePlan
    
    // Swift model fields for reference
    userName: mappedUser.userName || null,                          // Swift: userName
    token: mappedUser.token || null,                               // Swift: token
    avatar: mappedUser.avatar || null,                             // Swift: avatar
    start_use_date: mappedUser.start_use_date || null,             // Swift: start_use_date
    expired_date: mappedUser.expired_date || null,                 // Swift: expired_date
  };
};

/**
 * Get user information from localStorage with Swift model mapping
 * @returns {Object} Mapped user object or null
 */
export const getLoggedInUserMapped = () => {
  try {
    const authUser = localStorage.getItem("authUser");
    if (!authUser) return null;

    const rawUser = JSON.parse(authUser);
    return mapUserToSwiftModel(rawUser);
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
};

/**
 * Debug function to log user mapping information
 * @param {Object} rawUser - Raw user data from API
 * @param {Object} mappedUser - Mapped user data
 */
export const debugUserMapping = (rawUser, mappedUser) => {
  console.log('🔍 User Mapping Debug (Swift Reference):', {
    rawUser,
    mappedUser,
    shopInfo: getShopInfoFromUser(mappedUser),
    
    // Swift Model Fields Check (API Response)
    'API.login (Swift.userName)': rawUser?.login,
    'API.token (Swift.token)': rawUser?.token,
    'API.phone (Swift.phone)': rawUser?.phone,
    'API.address (Swift.address)': rawUser?.address,
    'API.store_name (Swift.storeName)': rawUser?.store_name,
    'API.avatar (Swift.avatar)': rawUser?.avatar,
    'API.create_date (Swift.start_use_date)': rawUser?.create_date,
    'API.expire_date (Swift.expired_date)': rawUser?.expire_date,
    'API.name (Swift.fullName)': rawUser?.name,
    'API.plan (Swift.privatePlan)': rawUser?.plan,
    'API.role (Swift.privateRole)': rawUser?.role,
    
    // Swift Model Mapped Fields
    'Swift.userName': mappedUser?.userName,
    'Swift.token': mappedUser?.token,
    'Swift.phone': mappedUser?.phone,
    'Swift.address': mappedUser?.address,
    'Swift.storeName': mappedUser?.storeName,
    'Swift.avatar': mappedUser?.avatar,
    'Swift.start_use_date': mappedUser?.start_use_date,
    'Swift.expired_date': mappedUser?.expired_date,
    'Swift.fullName': mappedUser?.fullName,
    'Swift.privatePlan': mappedUser?.privatePlan,
    'Swift.privateRole': mappedUser?.privateRole,
    
    // Swift Mapping Reference
    'Swift Mapping': {
      'userName <- map["login"]': rawUser?.login,
      'token <- map["token"]': rawUser?.token,
      'phone <- map["phone"]': rawUser?.phone,
      'address <- map["address"]': rawUser?.address,
      'storeName <- map["store_name"]': rawUser?.store_name,
      'avatar <- map["avatar"]': rawUser?.avatar,
      'start_use_date <- map["create_date"]': rawUser?.create_date,
      'expired_date <- map["expire_date"]': rawUser?.expire_date,
      'fullName <- map["name"]': rawUser?.name,
      'privatePlan <- map["plan"]': rawUser?.plan,
      'privateRole <- map["role"]': rawUser?.role,
    }
  });
}; 